/**
 * Client Request Success Page
 *
 * Displayed after a client successfully submits a property request.
 */

import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import Card from '../../components/base/Card';

export default function ClientRequestSuccess() {
  const { t, i18n } = useTranslation('clientRequests');
  const isRTL = i18n.language === 'ar';
  const location = useLocation();
  const requestNumber = location.state?.requestNumber || 'REQ-' + Math.random().toString(36).substring(2, 8).toUpperCase();

  const nextSteps = [
    {
      icon: 'ri-search-eye-line',
      title: t('success.step1', 'سيراجع الوسطاء المعتمدون طلبك'),
    },
    {
      icon: 'ri-message-3-line',
      title: t('success.step2', 'ستتلقى عروضاً من الوسطاء المناسبين'),
    },
    {
      icon: 'ri-handshake-line',
      title: t('success.step3', 'اختر العرض الأنسب لك وتواصل مع الوسيط'),
    },
  ];

  return (
    <div className="min-h-screen bg-[var(--pv-color-surface)]" dir={isRTL ? 'rtl' : 'ltr'}>
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Card variant="bordered" padding="lg" className="text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-check-line text-4xl text-green-500"></i>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-[var(--pv-color-text)] mb-2">
            {t('success.title', 'تم إرسال طلبك بنجاح!')}
          </h1>
          <p className="text-[var(--pv-color-text-muted)] mb-6">
            {t('success.subtitle', 'سيتواصل معك الوسطاء المعتمدون قريباً')}
          </p>

          {/* Request Number */}
          <div className="inline-block px-4 py-2 bg-[var(--pv-color-primary)]/10 rounded-lg mb-8">
            <p className="text-sm text-[var(--pv-color-text-muted)]">
              {t('success.requestNumber', 'رقم الطلب')}
            </p>
            <p className="text-lg font-bold text-[var(--pv-color-primary)]">{requestNumber}</p>
          </div>

          {/* What's Next */}
          <div className="border-t border-[var(--pv-border-soft)] pt-8 mt-4">
            <h2 className="text-lg font-semibold text-[var(--pv-color-text)] mb-6">
              {t('success.whatNext', 'ماذا بعد؟')}
            </h2>
            <div className="space-y-4">
              {nextSteps.map((step, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 text-start p-4 bg-[var(--pv-color-surface-soft)] rounded-xl"
                >
                  <div className="w-10 h-10 bg-[var(--pv-color-primary)]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className={`${step.icon} text-xl text-[var(--pv-color-primary)]`}></i>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-[var(--pv-color-primary)]">
                        {index + 1}
                      </span>
                      <span className="text-[var(--pv-color-text)]">{step.title}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link
              to="/my-requests"
              className="flex-1 px-6 py-3 bg-[var(--pv-color-primary)] text-white rounded-xl font-medium hover:shadow-lg transition-all text-center"
            >
              <i className="ri-list-check mr-2"></i>
              {t('success.viewMyRequests', 'عرض طلباتي')}
            </Link>
            <Link
              to="/client-requests/new"
              className="flex-1 px-6 py-3 bg-[var(--pv-color-surface-soft)] text-[var(--pv-color-text)] rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-all text-center"
            >
              <i className="ri-add-line mr-2"></i>
              {t('success.submitAnother', 'إرسال طلب آخر')}
            </Link>
          </div>

          <Link
            to="/"
            className="inline-block mt-4 text-[var(--pv-color-text-muted)] hover:text-[var(--pv-color-primary)] transition-colors"
          >
            {t('success.backToHome', 'العودة للرئيسية')}
          </Link>
        </Card>
      </main>
    </div>
  );
}
